import type { MediaCardProps } from '@/components/media/MediaCard';
import { SITE_ORIGIN } from '@/lib/seo/site-url';

/**
 * Fonte única de verdade da marca Johnny Vaz.
 * As bios são o texto aprovado em docs/JOHNNYVAZ/BIO.md — não reescrever por página.
 */

export interface SpeakingTopic {
  id: string;
  title: string;
  promise: string;
  forWhom: string;
  takeaways: string[];
  formatIds: string[];
}

export interface SpeakingFormat {
  id: string;
  name: string;
  duration: string;
  description: string;
}

export interface Credential {
  year: number;
  title: string;
  detail: string;
}

export interface PressPhoto {
  id: string;
  label: string;
  alt: string;
  /** Caminho base das versões WebP geradas por `npm run images:optimize`. */
  webp: string;
  /** PNG original, em alta, para download. */
  original: string;
  width: number;
  height: number;
}

export const speaker = {
  name: 'Johnny Vaz',
  jobTitle: 'Engenheiro de Software Sênior · Especialista em Inteligência Artificial',
  shortTitle: 'Engenheiro de Software Sênior · IA aplicada',
  roleLine: 'Lidera projetos de IA na PH3A Birô de Dados · Fundador da Geniaria · Autor do livro PPA',
  thesis: 'Inteligência não se baixa, se forma.',
  thesisSupport:
    'A tecnologia é o motor sob o capô, e o resultado é humano. É essa a conversa que eu levo para o palco.',
  site: SITE_ORIGIN,

  /** Cargo como sai no JSON-LD — idêntico ao `Person` publicado pela Geniaria. */
  schemaJobTitle: 'Engenheiro de Software Sênior',

  /**
   * A Geniaria é a mesma entidade nos dois domínios: o `@id` é o que ela publica
   * para si mesma, e `profile` é a página da pessoa lá (`/sobre`).
   */
  org: {
    name: 'Geniaria',
    url: 'https://geniaria.com.br',
    id: 'https://geniaria.com.br/#organization',
    profile: 'https://geniaria.com.br/sobre',
  },

  /** Empregador declarado na bio, além da Geniaria. A bio não dá URL. */
  employer: { name: 'PH3A Birô de Dados' },

  /** Formação com instituição — confirmada na BIO.md (H-013, 2026-10-04). */
  education: [
    { degree: 'Graduação em Análise e Desenvolvimento de Sistemas', school: 'FATEC Franca' },
    { degree: 'Bacharelado em Administração de Empresas', school: 'Estácio' },
  ],

  /** Temas declarados no JSON-LD — mesma lista do `Person` da Geniaria, derivada da bio. */
  knowsAbout: [
    'Inteligência artificial',
    'IA generativa em produção',
    'Arquitetura de sistemas',
    'Desenvolvimento fullstack',
    'Automação de processos',
    'Análise de dados',
    'Treinamento de IA para equipes',
    'Uso responsável de dados e LGPD',
    'Transição de carreira para a programação',
  ],

  book: {
    title: 'PPA: Sistema de Transição de Carreira Baseado em Trilha',
    about: 'Transição de carreira para a programação',
  },

  /**
   * Artigos de IA assinados por ele na Geniaria. Só entra URL que responde 200:
   * os satélites do pilar "IA para Builders" (/blog/vibe-coding e cia.) entram
   * aqui depois do deploy da Geniaria.
   */
  aiWriting: [
    {
      title: 'Vibe coding: o que é, de onde veio e o que quebra em produção',
      url: 'https://geniaria.com.br/blog/vibe-coding',
      summary: 'Criar software descrevendo o que você quer para a IA: a origem do termo, onde funciona e os riscos antes de ter usuários reais.',
    },
    {
      title: 'Agentes de IA: o que são, como funcionam e quais os riscos',
      url: 'https://geniaria.com.br/blog/agentes-de-ia',
      summary: 'O modelo que decide e executa ações com ferramentas, em ciclo — a diferença para o chatbot, o que é MCP e como limitar o risco.',
    },
    {
      title: 'IA no trabalho: um guia para formar a equipe com critério',
      url: 'https://geniaria.com.br/blog/ia-no-trabalho',
      summary: 'Por que acesso à ferramenta não basta, e as competências para a equipe usar, verificar e construir com IA com responsabilidade.',
    },
  ],

  bios: {
    short:
      'Johnny Vaz é Engenheiro de Software Sênior, especialista em Inteligência Artificial e fundador da Geniaria — plataforma de cursos de desenvolvimento humano, que também opera com a marca do cliente e leva treinamento de IA às equipes nas empresas. Lidera projetos de IA na PH3A e é autor do livro PPA.',
    medium:
      'Johnny Vaz é Engenheiro de Software Sênior e especialista em Inteligência Artificial. Fundou a Geniaria, plataforma de cursos com três frentes: um acervo próprio de cursos de desenvolvimento humano, onde um mapa de autoconhecimento indica por onde começar; a mesma plataforma operando com a marca e o domínio de cada cliente; e treinamento de IA ao vivo para equipes dentro das empresas — gente que escreve os próprios pedidos, monta contexto e questiona a resposta. Lidera projetos de IA na PH3A Birô de Dados e é autor do livro PPA: Sistema de Transição de Carreira Baseado em Trilha, nascido da sua própria migração para a programação. Formado em Análise e Desenvolvimento de Sistemas e em Administração de Empresas.',
    full: [
      'Johnny Vaz é Engenheiro de Software Sênior e especialista em Inteligência Artificial. Sua tese é mais simples do que o mercado costuma vender: inteligência não se baixa, se forma — a tecnologia é o motor sob o capô, e o resultado é humano.',
      'Fundou e lidera a Geniaria, plataforma de cursos voltada à formação da pessoa inteira — mente, caráter e propósito. A Geniaria entrega em três frentes: um acervo de cursos próprios, com trilha de desenvolvimento humano ancorada em ciência cognitiva e um mapa de autoconhecimento que indica por onde cada pessoa deve começar; a plataforma operando com a marca do cliente, em domínio próprio, com login corporativo e matrícula automática da equipe; e treinamento de IA para equipes, ao vivo, dentro da empresa, formando o time em três competências — escrever os próprios pedidos, montar bons contextos e ser crítico com as saídas — com uso responsável de dados (LGPD) como prioridade, não como anexo.',
      'Na PH3A Birô de Dados, lidera projetos de Inteligência Artificial aplicados a análise de dados e automação de processos. Sua expertise técnica abrange arquitetura de sistemas, desenvolvimento fullstack e aplicações de IA generativa em produção.',
      'É autor do livro PPA: Sistema de Transição de Carreira Baseado em Trilha. A obra nasce da sua própria trajetória: migrou da área de suporte e administração para a programação e, a partir dessa experiência real, estruturou um método para quem quer mudar de área com estratégia e consistência.',
      'Graduado em Análise e Desenvolvimento de Sistemas e em Administração de Empresas.',
    ].join('\n\n'),
  },

  pillars: [
    { label: 'IA aplicada', text: 'Lidera projetos de Inteligência Artificial na PH3A Birô de Dados, em análise de dados e automação de processos.' },
    { label: 'Geniaria', text: 'Fundou a plataforma de cursos que forma a pessoa inteira e leva treinamento de IA às equipes dentro das empresas.' },
    { label: 'Autoria', text: 'Escreveu o PPA: Sistema de Transição de Carreira Baseado em Trilha, método nascido da própria migração para a programação.' },
    { label: 'Formação', text: 'Graduado em Análise e Desenvolvimento de Sistemas e em Administração de Empresas.' },
  ],

  formats: [
    { id: 'keynote', name: 'Keynote', duration: '45 a 60 minutos', description: 'Palestra de abertura ou encerramento, com espaço para perguntas da plateia.' },
    { id: 'workshop', name: 'Workshop', duration: '3 a 4 horas', description: 'Mão na massa: cada participante aplica ao próprio trabalho durante o encontro.' },
    { id: 'incompany', name: 'Treinamento in-company', duration: 'Programa ao vivo, turma fechada', description: 'O programa da Geniaria dentro da empresa, com acompanhamento ao longo dos encontros.' },
  ] as SpeakingFormat[],

  topics: [
    {
      id: 'inteligencia-se-forma',
      title: 'Inteligência não se baixa, se forma',
      promise: 'Por que a corrida por ferramentas não produz gente mais inteligente — e o que produz.',
      forWhom: 'Eventos de tecnologia, educação e liderança',
      takeaways: [
        'Por que o ganho de produtividade evapora quando ninguém entende o que foi entregue',
        'A diferença entre operar um modelo e formar julgamento',
        'Como decidir o que vale automatizar e o que vale ensinar',
      ],
      formatIds: ['keynote'],
    },
    {
      id: 'equipe-usando-ia',
      title: 'Sua equipe usando IA de verdade',
      promise: 'As três competências que separam quem usa IA de quem apenas assinou uma ferramenta de IA.',
      forWhom: 'Empresas que já compraram a ferramenta e não veem resultado',
      takeaways: [
        'Escrever o próprio pedido, em vez de colar o prompt de outra pessoa',
        'Montar contexto: o que o modelo precisa saber antes de responder',
        'Questionar a saída — e tratar dado sensível com LGPD desde o primeiro encontro',
      ],
      formatIds: ['workshop', 'incompany'],
    },
    {
      id: 'criou-com-ia',
      title: 'Você criou com IA. Mas sabe o que não sabe?',
      promise: 'A IA entrega o código pronto e fica em silêncio sobre o que vai quebrar depois.',
      forWhom: 'Founders, product people e profissionais não-técnicos que constroem com IA',
      takeaways: [
        'Onde autenticação frágil, dado exposto e ausência de backup aparecem no que a IA gera',
        'As perguntas que revelam risco antes de ir para produção',
        'Conformidade como decisão de projeto, não como anexo do final',
      ],
      formatIds: ['keynote', 'workshop'],
    },
  ] as SpeakingTopic[],

  credentials: [
    { year: 2019, title: '1º lugar — Hackathon Magazine Luiza', detail: 'Solução de otimização logística' },
    { year: 2017, title: 'Vencedor — Startup Weekend', detail: 'Solução para educação' },
  ] as Credential[],

  media: [
    {
      title: 'Entrevista na Record Litoral',
      date: 'Julho de 2025',
      description: 'Johnny Vaz discute o impacto da Inteligência Artificial nos negócios, automação de processos e como a tecnologia está moldando o futuro das empresas.',
      tags: ['IA nos Negócios', 'Automação', 'Entrevista'],
      link: 'https://www.youtube.com/watch?v=5HxNyi0rgDw',
      thumbnail: 'https://img.youtube.com/vi/5HxNyi0rgDw/maxresdefault.jpg',
    },
    {
      title: 'Participação no PodEsquenta',
      date: 'Agosto de 2025',
      description: 'Uma conversa aprofundada sobre a democratização da IA, casos práticos de uso em empresas e o futuro da carreira em tecnologia.',
      tags: ['Inteligência Artificial', 'Carreira em TI', 'Podcast'],
      link: 'https://www.youtube.com/live/1M21UEMUzFU',
      thumbnail: 'https://img.youtube.com/vi/1M21UEMUzFU/maxresdefault.jpg',
    },
  ] as MediaCardProps[],

  pressKit: {
    logo: '/images/logo/logo-jv-tree.png',
    photos: [
      {
        id: 'conceito1',
        label: 'Retrato — terno navy',
        alt: 'Johnny Vaz de blazer azul-marinho e camiseta branca, em fundo de estúdio cinza',
        webp: '/images/speaker/conceito1',
        original: '/images/press/conceito1.png',
        width: 1142,
        height: 1378,
      },
      {
        id: 'conceito2',
        label: 'Retrato — terno grafite',
        alt: 'Johnny Vaz de blazer cinza-grafite e camisa preta, em fundo de estúdio cinza',
        webp: '/images/speaker/conceito2',
        original: '/images/press/conceito2.png',
        width: 1122,
        height: 1402,
      },
    ] as PressPhoto[],
  },

  social: {
    linkedin: 'https://www.linkedin.com/in/johnnyvaz/',
    github: 'https://github.com/johnnyvaz',
    youtube: 'https://www.youtube.com/@Johnny_Vaz',
    instagram: 'https://www.instagram.com/johnnyvaz1/',
  },

  contact: {
    email: 'contato@johnnyvaz.com.br',
    whatsapp: '5516997787674',
    calendar: 'https://calendar.app.google/11YBTxaW9hU3yJdJ8',
  },

  links: {
    builder: 'https://geniaria.com.br/courses/c0c0c0c0-0101-0000-0000-000000000000',
    mentoria: '/mentoria',
    speaking: '/palestras',
    press: '/imprensa',
  },
} as const;

/** srcSet pronto para as larguras geradas pelo script de imagens. */
export function photoSrcSet(photo: PressPhoto): string {
  return [640, 960, 1280].map((w) => `${photo.webp}-${w}.webp ${w}w`).join(', ');
}

export function formatById(id: string): SpeakingFormat | undefined {
  return speaker.formats.find((f) => f.id === id);
}
